import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.function.name.js';
import 'core-js/modules/es.object.assign.js';
import { __rest } from 'tslib';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import React from 'react';
import { Checkbox as Checkbox$1 } from '@mui/joy';
import classNames from 'classnames';
import Color from 'color';
import { getThemeClassNames } from '@cognite/cogs-utils';
import { Skeleton } from '../skeleton/skeleton.js';
import { IndeterminateIcon, CheckmarkIcon } from './icons.js';

/**
 * Checkbox
 * =============================================================================
 * Checkboxes allow users to select one or more items from a set. Checkboxes are
 * typically used to toggle options on or off. Secondly, checkboxes can be used
 * to select one or more items from a set of items.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/storybook/?path=/docs/core_core-checkbox--docs)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Checkbox checked name="checkbox1">
 *   My checkbox
 * </Checkbox>
 *
 * <Checkbox indeterminate onChange={changeHandler}>
 *   My indeterminate checkbox
 * </Checkbox>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param checked         - Check or uncheck the checkbox.
 * @param color           - The color of the checkbox. Don't over-use this!
 * @param defaultChecked  - If true, the checkbox is checked by default.
 * @param disabled        - Disable the checkbox.
 * @param inverted        - Renders the checkbox as an inverted checkbox.
 * @param indeterminate   - Renders the checkbox as an indeterminate checkbox.
 * @param name            - Native `name` HTML attribute. (Also used as the id)
 * @param onChange        - The function fired when the checkbox is changed.
 * @param readOnly        - If true, the user can't change the checkbox.
 * @param skeleton        - The skeleton version of the checkbox.
 * @param style           - Overwrite and extend the default styling with custom styles.
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/storybook/?path=/docs/core_core-checkbox--docs)
 */
var Checkbox = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var checked = _a.checked,
    children = _a.children,
    className = _a.className,
    color = _a.color,
    defaultChecked = _a.defaultChecked,
    disabled = _a.disabled,
    id = _a.id,
    inverted = _a.inverted,
    indeterminate = _a.indeterminate,
    name = _a.name,
    _onChange = _a.onChange,
    readOnly = _a.readOnly,
    independentIndeterminateState = _a.independentIndeterminateState,
    skeleton = _a.skeleton,
    style = _a.style,
    themeProps = _a.themeProps,
    otherProps = __rest(_a, ["checked", "children", "className", "color", "defaultChecked", "disabled", "id", "inverted", "indeterminate", "name", "onChange", "readOnly", "independentIndeterminateState", "skeleton", "style", "themeProps"]);
  return skeleton ? jsxs(Fragment, {
    children: [jsx(Skeleton.Rectangle, {
      width: "16px",
      height: "16px"
    }), children && jsx(Skeleton.Rectangle, {
      style: {
        marginLeft: 10,
        width: 100
      },
      height: "16px"
    })]
  }) : jsx(Checkbox$1, Object.assign({
    id: id || name,
    className: classNames.apply(undefined, ['cogs-v10', 'cogs-checkbox', disabled && 'cogs-checkbox--disabled', indeterminate && 'cogs-checkbox--indeterminate', independentIndeterminateState && indeterminate && 'cogs-checkbox--indeterminate-forced'].concat(getThemeClassNames(inverted, themeProps), [className])),
    label: children,
    ref: ref,
    style: color ? Object.assign({
      '--Checkbox-color': Color(color).hex(),
      '--Checkbox-color-hover': Color(color).darken(0.15).hex(),
      '--Checkbox-color-pressed': Color(color).darken(0.3).hex(),
      '--Checkbox-border-hover': Color(color).lighten(0.1).hex(),
      '--Checkbox-border-pressed': Color(color).darken(0.1).hex()
    }, style) : Object.assign({}, style),
    defaultChecked: defaultChecked,
    indeterminateIcon: jsx(IndeterminateIcon, {}),
    checked: checked,
    disabled: disabled,
    readOnly: readOnly,
    name: name,
    onChange: function onChange(e) {
      return _onChange && _onChange(e, e.target.checked);
    },
    indeterminate: indeterminate,
    checkedIcon: jsx(CheckmarkIcon, {}),
    slotProps: {
      checkbox: {
        className: 'cogs-checkbox__checkbox'
      },
      input: {
        className: 'cogs-checkbox__input'
      },
      label: {
        className: 'cogs-checkbox__label'
      },
      action: {
        className: 'cogs-checkbox__action'
      }
    }
  }, otherProps));
});

export { Checkbox };
